import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * useResizable Hook
 *
 * Manages panel resize functionality with proper event cleanup.
 * Uses refs to avoid stale closure issues with event listeners.
 *
 * @param {Object} options - Configuration options
 * @param {boolean} options.enabled - Whether resizing is enabled
 * @param {number} options.minWidth - Minimum panel width
 * @param {number} options.maxWidth - Maximum panel width
 * @param {number} options.defaultWidth - Default panel width
 * @param {Function} options.onResize - Callback when panel is resized
 *
 * @returns {Object} Resize state and handlers
 */
export const useResizable = ({
  enabled = true,
  minWidth = 400,
  maxWidth = 800,
  defaultWidth = 480,
  onResize = null,
} = {}) => {
  const [panelWidth, setPanelWidth] = useState(defaultWidth);
  const [isResizing, setIsResizing] = useState(false);

  // Refs for resize state (avoids stale closures)
  const isResizingRef = useRef(false);
  const resizeStartX = useRef(0);
  const resizeStartWidth = useRef(0);
  const onResizeRef = useRef(onResize);

  // Keep onResize ref updated
  useEffect(() => {
    onResizeRef.current = onResize;
  }, [onResize]);

  /**
   * Handle mouse move during resize
   * Uses refs to get current values, avoiding stale closure issues
   */
  const handleResizeMove = useCallback(
    (e) => {
      if (!isResizingRef.current) return;

      const deltaX = resizeStartX.current - e.clientX;
      const newWidth = Math.min(
        maxWidth,
        Math.max(minWidth, resizeStartWidth.current + deltaX)
      );

      setPanelWidth(newWidth);

      if (onResizeRef.current) {
        onResizeRef.current(newWidth);
      }
    },
    [minWidth, maxWidth]
  );

  /**
   * Handle mouse up to end resize
   */
  const handleResizeEnd = useCallback(() => {
    isResizingRef.current = false;
    setIsResizing(false);
    document.body.classList.remove('gigabot-resizing');

    // Remove listeners
    document.removeEventListener('mousemove', handleResizeMove);
    document.removeEventListener('mouseup', handleResizeEnd);
  }, [handleResizeMove]);

  /**
   * Handle mouse down to start resize
   */
  const handleResizeStart = useCallback(
    (e) => {
      if (!enabled) return;

      e.preventDefault();
      isResizingRef.current = true;
      setIsResizing(true);
      document.body.classList.add('gigabot-resizing');
      resizeStartX.current = e.clientX;
      resizeStartWidth.current = panelWidth;

      // Add listeners
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
    },
    [enabled, panelWidth, handleResizeMove, handleResizeEnd]
  );

  /**
   * Reset panel width to default
   */
  const resetWidth = useCallback(() => {
    setPanelWidth(defaultWidth);
    if (onResizeRef.current) {
      onResizeRef.current(defaultWidth);
    }
  }, [defaultWidth]);

  /**
   * Cleanup effect: Remove event listeners on unmount
   */
  useEffect(() => {
    return () => {
      document.body.classList.remove('gigabot-resizing');
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
    };
  }, [handleResizeMove, handleResizeEnd]);

  return {
    // State
    panelWidth,
    isResizing,

    // Handlers
    handleResizeStart,
    resetWidth,

    // Setters (for external control)
    setPanelWidth,
  };
};

export default useResizable;
